import { SessionDetails }                   from '../../../../models/program/session-details';
import { ErrorService }                     from '../../../../shared/services/error.service';
import { ProgramService }                   from '../../../../shared/services/program.service';
import { TimezoneService }                  from '../../../../shared/services/timezones.service';
import { FmRelease }                        from '../../../../models/program/fm-release';
import { TeamNameList }                     from '../../../../models/program/team-name-list';
import { ProgramSingle }                    from '../../../../models/program/program-single';
import { UtilsService }                     from '../../../../shared/utils.service';
import { EditionListService }               from '../../../../editions/edition.service';
import { ActivatedRoute, Params }           from '@angular/router';

import { 
  FormBuilder, 
  FormGroup,
  FormArray,
  Validators,
  FormControl,
  NG_VALUE_ACCESSOR
}                                           from '@angular/forms';
import { 
  Component, 
  OnInit, 
  Input, 
  Output, 
  EventEmitter
}                                           from '@angular/core';
import { PermissionsService } from '../../../../shared/services/permissions.service';

@Component({
  selector: 'app-edit-session',
  templateUrl: './edit-session.component.html',
  styleUrls: ['./edit-session.component.less'],
  providers: [ProgramService, UtilsService]
})

export class EditSessionComponent implements OnInit {
  @Input() session?: SessionDetails;
  @Output() update: EventEmitter<SessionDetails> = new EventEmitter<SessionDetails>();
  @Output() close: EventEmitter<void> = new EventEmitter<void>();
  sessionData: FormGroup;
  wordLists: TeamNameList[] = [];
  saving: boolean;
  saved: boolean;
  programId: number;
  programLoaded: boolean;
  errors: string[] = [];
  fms: FmRelease[] = [];
  fmReleases = [];
  simplifiedInterface: boolean;

  constructor(
    private programService: ProgramService,
    public tz: TimezoneService,
    private _fb: FormBuilder,
    private route: ActivatedRoute,
    private utils: UtilsService,
    private errorService: ErrorService,
    private editionListService: EditionListService,
    private permissionsService: PermissionsService
  ) {}

  ngOnInit(): void {
    this.route.params.forEach((params: Params) => {
      this.programId = +params['programId'];
    });
    this.simplifiedInterface = this.permissionsService.simplifiedSessionManagement;
    this.getWordLists();
    this.getProgram();
    this.initForm();
    this.setUpdateActions();
  }

  private initForm() {
    this.fmReleases = this.originalFmReleases;
    this.sessionData = this.defaultSessionData;
    this.setScheduled();
  }

  get sessionStarted() {
    return this.session && this.session.group_set_id;
  }

  get originalFmReleases() {
    if (this.session) {
      return this.session.fm_releases as FmRelease[];
    }
    return [];
  }
  get fieldText() {
    return {
      name: {label: 'Session Name', name: 'Name'},
      automatic: {label: 'Automatic Team Creation', name: 'Automatic Team Creation'},
      timezone: {label: 'Set a Time Zone for the fields below', name: 'Time zone'},
      start_date: {label: 'Session Start Date', name: 'Session Start Date'},
      end_date: {label: 'Session End Date', name: 'Session End Date'},
      joining_opens: {label: 'Enrollment Start Date', name: 'Enrollment Start Date'},
      joining_closes: {label: 'Enrollment End Date', name: 'Enrollment End Date'},
      min_team_size: {label: 'Minimum Team Size (50 - 100 recommended)', name: 'Minimum Team Size'},
      team_name_list: {label: 'Word List', name: 'Word List'},
      team_name_list_2: {label: 'Second Word List (Optional)', name: 'Second Word List'},
      team_name_prefix: {label: 'Team Name Prefix', name: 'Team Name Prefix'},
      scheduled: {label: 'Release Field Manuals on a custom schedule', name: 'Scheduled'},
      unscheduled: {label: 'Release Field Manuals at session start', name: 'Unscheduled'}
    }
  }

  get defaultSessionData() {
    return this._fb.group({
      name: [{
        value: this.session ? this.session.name : null,
        disabled: this.sessionStarted
      }, Validators.required],
      timezone: [{
        value: this.session ? this.session.timezone: null,
        disabled: this.sessionStarted
      }, Validators.required],
      start_date: [{
        value: this.session ? this.session.start_date: null,
        disabled: this.sessionStarted
      }, Validators.required],
      end_date: [{
        value: this.session ? this.session.end_date: null,
        disabled: false
      }, Validators.required],
      joining_opens: [{
        value: this.session ? this.session.joining_opens: null,
        disabled: this.sessionStarted
      }, Validators.required],
      joining_closes: [{
        value: this.session ? this.session.joining_closes: null,
        disabled: false
      }, Validators.required],
      min_team_size: [{
        value: this.session ? this.session.min_team_size : 50,
        disabled: this.sessionStarted
      }],
      team_name_list: [{
        value: this.session && this.session.team_name_lists.length ? this.session.team_name_lists[0] : null,
        disabled: this.sessionStarted
      }],
      team_name_list_2: [{
        value: this.session && this.session.team_name_lists.length > 1 ? this.session.team_name_lists[1] : null,
        disabled: this.sessionStarted
      }],
      team_name_prefix: [{
        value: this.session ? this.session.team_name_prefix : null,
        disabled: this.sessionStarted
      }],
      automatic: [{
        value: this.session ? this.session.automatic: true,
        disabled: false
      }, Validators.required],
      scheduled: [{
        value: false, 
        disabled: this.sessionStarted
      }],
      unscheduled: [{
        value: true, 
        disabled: this.sessionStarted
      }],
    });
  }

  get data() {
    return this.sessionData.controls;
  }
  get showAutomatic() {
    return this.data['automatic'].value;
  }

  get showSchedule() {
    return this.data['scheduled'].value;
  }

  get enrollmentGap() {
    return this.data['automatic'].value && (this.data['joining_closes'].value < this.data['start_date'].value);
  }

  get joiningClosesAfterStart() {
    if (!this.data['joining_closes'].value || !this.data['start_date'].value) {
      return false;
    }
    return this.data['joining_closes'].value > this.data['start_date'].value;
  }

  get joiningOpensAfterStart() {
    if (!this.data['joining_opens'].value || !this.data['start_date'].value) {
      return false;
    }
    return this.data['joining_opens'].value > this.data['start_date'].value;
  }

  get joiningEndsBeforeJoiningStarts() {
    if (!this.data['joining_opens'].value || !this.data['joining_closes'].value) {
      return false;
    }
    return this.data['joining_opens'].value > this.data['joining_closes'].value;
  }

  setScheduled(): void {
    let scheduled;
    if (this.session) {
      scheduled = !!this.session.fm_releases.filter(r => r.release !== this.session.start_date).length;
    } else {
      scheduled = false;
    }
    this.data['scheduled'].setValue(scheduled);
    this.data['unscheduled'].setValue(!scheduled);
  }

  setUpdateActions() {
    this.data['start_date'].valueChanges.subscribe(value => {
      if (!this.data['joining_closes'].value) {
        this.data['joining_closes'].setValue(value);
      }
    });
    this.data['automatic'].valueChanges.subscribe(value => {
      if (this.data['automatic'].value && this.data['joining_closes'].value < this.data['start_date'].value) {
        this.data['joining_closes'].setValue(this.data['start_date'].value);
      }
    });
    this.data['scheduled'].valueChanges.subscribe(value => {
      if (this.data['unscheduled'].value == value) {
        this.data['unscheduled'].setValue(!value);
      }
    });
    this.data['unscheduled'].valueChanges.subscribe(value => {
      if (this.data['scheduled'].value == value) {
        this.data['scheduled'].setValue(!value);
      }
    });
  }

  updateFmReleases(releases: FmRelease[]): void {
    this.fmReleases = releases;
  }

  releaseTabSelected(title: string): void {
    this.data['scheduled'].setValue(title == "Custom Schedule");
    if (!this.data['scheduled'].value) {
      this.fmReleases = this.originalFmReleases;
    }
  }

  getWordLists(): void {
    this.programService
      .getTeamNameLists()
      .subscribe(lists => {
        if (lists) {
          this.wordLists = lists;
        }
      });
  }

  getProgram(): void {
    this.programService
      .getProgram(this.programId)
      .subscribe(program => {
        this.fms = program.fms.map((fm) => {
          return { 
            title: fm.title,
            id: fm.id,
            release: null
          };
        });
        this.programLoaded = true
      });
  }

  validateInputs(): void {
    this.errors = [];
    for (const key of Object.keys(this.data)) {
      if (this.data[key].errors) {
        this.errors.push(`${this.fieldText[key].name} is required.`);
      }
    };
    this.errors = this.errors.slice(); // Trigger UI update (why isn't this working?)
  }

  getSessionData(): SessionDetails {
    let updatedSession = this.session ? JSON.parse(JSON.stringify(this.session)) : new SessionDetails;
    updatedSession.fm_releases = this.showSchedule ? this.fmReleases.filter(fm => fm.release !== undefined) : []; // Filter out FMs that should not be released
    for (let key in this.data) {
      updatedSession[key] = this.data[key].value;
    }

    // convert first and optional second word lists to array
    let teamNameList = this.data['team_name_list'].value;
    let teamNameList2 = this.data['team_name_list_2'].value;
    if (teamNameList) {
      updatedSession.team_name_lists = [teamNameList];
      if (teamNameList2) {
        updatedSession.team_name_lists.push(teamNameList2);
      }
    }
    else {
      updatedSession.team_name_lists = [];
    }

    return updatedSession;
  }

  emitDismissed(): void {
    this.close.emit();
  }

  postNewSession(updatedSession: SessionDetails): void {
    this.programService
      .submitNewSession(updatedSession, this.programId)
      .subscribe(
      (resp) => {
        this.saving = false;
        this.saved = true;
        this.sessionData.markAsPristine();
        this.update.emit(updatedSession);
        this.editionListService.refreshCurrentEdition();
      },
      (error) => {
        this.errors = this.errorService.messages(error, "Something went wrong while saving your session. Please try again.");
        this.saving = false;
      }
    );
  }

  updateExistingSession(updatedSession: SessionDetails): void {
    this.programService
      .updateSession(updatedSession, this.programId, this.session.id)
      .subscribe(
      (resp) => {
        this.saving = false;
        this.saved = true;
        this.sessionData.markAsPristine();
        this.update.emit(updatedSession);

        // Update the map only if the name has been changed
        const mapName = this.editionListService.getEditionMap().getSession(this.session.id).name;
        if (mapName !== updatedSession.name) {
          this.editionListService.refreshCurrentEdition();
        }
      },
      (error) => {
        this.errors = this.errorService.messages(error, "Something went wrong while saving your session. Please try again.");
        this.saving = false;
      }
    );
  }

  saveSession(): void {
    if (this.saving) {
      return;
    }
    this.saving = true;

    this.validateInputs()
    if (this.errors.length) {
      this.saving = false;
      return;
    }
    let updatedSession = this.getSessionData();
    if (this.session) {
      this.updateExistingSession(updatedSession);
    }
    else {
      this.postNewSession(updatedSession);
    }
  }

  getExampleTeamName(): string {
    if (this.wordLists) {
      let list1 = this.wordLists.find(list => list.id === this.data['team_name_list'].value);
      let list2 = this.wordLists.find(list => list.id === this.data['team_name_list_2'].value);
      let prefix = this.data['team_name_prefix'].value;

      let name = prefix || (list1 ? '' : 'Team');
      name += list1 ? ' ' + list1.example : ' 1';
      name += list1 && list2 ? ' ' + list2.example : '';
      return name;
    }
  }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/dashboard/program-list/sessions/edit-session/edit-session.component.ts